import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger, } from '@frontend/shadcn/components/ui/tooltip';
import { BoltIcon, GiftIcon, SackDollarIcon, TagsIcon } from '@revfluence/fresh-icons/solid';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@frontend/shadcn/components/ui/accordion';
import { Button } from '@frontend/shadcn/components/ui/button';
import { ClockIcon, TrophyIcon } from '@revfluence/fresh-icons/solid/esm';
import Tag from '@frontend/app/refresh-components/Tag';
import { BOOST_TYPE, BoostRewardAction } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { useBulkActionBoostRewards } from '@frontend/applications/AffiliatesApp/hooks';
import { message } from '@revfluence/fresh';
import { ArrowsRotateIcon } from '@revfluence/fresh-icons/regular';
import { GetAllBoostData_boostDataForAllAffiliatesInOffer_boost } from '@frontend/applications/AffiliatesApp/queries/types/GetAllBoostData';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { ClientFeature } from '@frontend/app/constants/clientFeatures';
import { useAuth } from '@frontend/context/authContext';
import { useApolloClient } from '@frontend/applications/ProductFulfillmentApp/context';
import { logger } from '../../../../../../../common/Logger';
import { MemberBoosts } from '../RefreshMemberConversionDrawer';
import PFAApprovalDrawer from '../../PFAApprovalDrawer';
import { TMember } from '../../MembersWizard/types';
import { useAppContext } from '@/context/AppContext';
import { useGetOrderById } from '@/applications/ProductFulfillmentApp/hooks';
import AlertPFAReject from '../../PFAApprovalDrawer/AlertPFAReject';
import { useBulkApproveRejectOrderRequest } from '@/applications/ProductFulfillmentApp/hooks';
import { BulkApproveRejectOperation } from '@/applications/ProductFulfillmentApp/types/globalTypes';
import { TSelectedAffliate } from '../../MemberTable/types';

interface MemberBoostDetailsProps {
  memberBoosts: MemberBoosts
  onClose?: () => void;
  refetchData?: () => void;
  refresh?: () => void;
  selectedMemberConversion?: TSelectedAffliate;
  refetchBoostsPerformanceData?: () => void;
  offerId: number;
}

// Separate component for order-related functionality to avoid hooks in loops
interface OrderCardProps {
  reward: any;
  onViewOrder: (orderId: string) => void;
  onReject: (pfaOrderId: string) => void;
  isRejecting: boolean;
}
const getBoostIcon = (boostType: string) => {
  switch (boostType) {
    case BOOST_TYPE.DISCOUNT_BOOST:
      return (
        <div className="flex items-center justify-center rounded-full bg-teal-4 h-[54px] w-[54px]">
          <TagsIcon className="text-grey-0 h-6 w-6 " />
        </div>
      );
    case BOOST_TYPE.CASHBACK_BOOST:
      return (
        <div className="flex items-center justify-center rounded-full bg-yellow-4 h-[54px] w-[54px]">
          <SackDollarIcon className="h-6 w-6  text-grey-0" />
        </div>
      );
    case BOOST_TYPE.PFA_BOOST:
      return (
        <div className="flex items-center justify-center rounded-full bg-magenta-4 h-[54px] w-[54px]">
          <GiftIcon className="text-grey-0 h-6 w-6 " />
        </div>
      );
    default:
      return <BoltIcon className="h-4 w-4 text-gray-500" />;
  }
};
const OrderCard: React.FC<OrderCardProps> = ({ reward, onViewOrder, onReject, isRejecting }) => {
  const appContext = useAppContext();
  const pfaApolloClient = appContext?.pfaApolloClient;

  const pfaOrderId = reward?.boostRewardMetadata?.pfaOrderId;

  // Hook call at top level of this component
  const { order } = useGetOrderById({
    variables: { id: pfaOrderId },
    client: pfaApolloClient,
    skip: !pfaOrderId, // Skip if no order ID
  });

  if (!pfaOrderId || order?.orderRequestStatus !== 'PENDING') {
    return null;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const createdDate = order?.createdDate ? formatDate(order.createdDate) : '';

  return (
    <Card className="bg-[#f9f9fa] rounded-xl p-0 border-0 mb-2">
      <CardContent className="flex flex-col items-start gap-2 p-3">
        <div className="flex h-6 items-center justify-between w-full">
          <p className="text-xs text-[#16282d] font-normal leading-5 m-0">
            Creator submitted the order on {createdDate}
          </p>
          <Badge className="bg-[#f8e8b8] text-[#16282d] font-normal text-xs rounded-lg px-2 py-0.5 h-auto">
            Review Order Request
          </Badge>
        </div>
        <div className="flex items-start gap-2">
          <Button
            className="bg-[#16282d] text-white rounded-lg px-4 py-1 h-auto font-medium text-sm"
            onClick={() => onViewOrder(pfaOrderId)}
          >
            View & Approve
          </Button>
          <Button
            variant="ghost"
            className="text-[#eb6661] px-4 py-1 h-auto font-medium text-sm rounded-lg"
            onClick={() => onReject(pfaOrderId)}
            disabled={isRejecting}
          >
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Separate component for reward item to handle order status properly
interface RewardItemProps {
  reward: any;
  boost: GetAllBoostData_boostDataForAllAffiliatesInOffer_boost;
  rewardIndex: number;
  totalRewards: number;
  onApprove: (id: number, action: BoostRewardAction) => void;
}

const RewardItem: React.FC<RewardItemProps> = ({ reward, boost, rewardIndex, totalRewards, onApprove }) => {
  const { pfaApolloClient } = useAppContext();

  const pfaOrderId = reward?.boostRewardMetadata?.pfaOrderId;

  // Hook call at top level of this component
  const { order } = useGetOrderById({
    variables: { id: pfaOrderId },
    client: pfaApolloClient,
    skip: !pfaOrderId || boost?.boostMetadata?.pfaBoostType !== 'PRODUCT_CATALOG' || boost.boostType !== 'PFA_BOOST',
  });

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'PENDING':
        return { label: 'Waiting for approval', bgColor: 'bg-yellow-2', textColor: 'text-primary' };
      case 'APPROVED':
        return { label: 'Awarded', bgColor: 'bg-green-2', textColor: 'text-primary' };
      case 'REJECTED':
        return { label: 'Rejected', bgColor: 'bg-red-2', textColor: 'text-primary' };
      case 'ERROR':
        return { label: 'Error', bgColor: 'bg-red-2', textColor: 'text-primary' };
      default:
        return { label: 'Unknown', bgColor: 'bg-grey-2', textColor: 'text-primary' };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'text-yellow-4';
      case 'APPROVED':
        return 'text-green-4';
      case 'REJECTED':
        return 'text-red-4';
      case 'ERROR':
        return 'text-red-4';
      default:
        return 'text-grey-4';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const statusTag = getStatusTag(reward.status);
  const statusIconClass = getStatusIcon(reward.status);

  // Check if we should show view order button
  let viewOrderButton = null;
  if (
    boost?.boostMetadata?.pfaBoostType === 'PRODUCT_CATALOG' &&
    boost.boostType === 'PFA_BOOST' &&
    pfaOrderId &&
    order
  ) {
    const orderStatusUrl = order.raw?.order_status_url;
    if (orderStatusUrl) {
      viewOrderButton = (
        <Button
          variant="outline"
          className="w-max mt-2 ml-2"
          onClick={() => window.open(orderStatusUrl, '_blank')}
        >
          View Order
        </Button>
      );
    }
  } else if (boost?.boostType === 'DISCOUNT_BOOST' && reward?.secureCode?.isRedeemed && reward?.shopifyOrderEvent?.orderStatusUrl) {
    const orderStatusUrl = reward?.shopifyOrderEvent?.orderStatusUrl;
    viewOrderButton = (
      <Button
        variant="outline"
        className="w-max mt-2 ml-2"
        onClick={() => window.open(orderStatusUrl, '_blank')}
      >
        View Order
      </Button>
    );
  }
  console.log(reward);
  return (
    <div className="flex flex-row items-stretch gap-3">
      <div className="flex flex-col items-center w-7 relative">
        <ClockIcon className={`h-4 w-4 ${statusIconClass}`} />
        {rewardIndex < totalRewards - 1 && (
          <div className="flex w-px border-l-2 border-dashed border-gray-200 h-full" />
        )}
      </div>
      <div className="flex-1">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
          <span className="font-medium text-sm text-primary">
            {boost.boostType === 'DISCOUNT_BOOST' ? 'Discount Code' : 'Reward'}
          </span>
          {(boost.boostType === BOOST_TYPE.CASHBACK_BOOST && reward?.cashback?.amount) && <span className="text-primary text-sm">$ {reward?.cashback?.amount}</span>}
          <span className="text-primary text-sm hidden sm:inline">•</span>
          <span className="text-primary text-sm">
            {reward.status === 'APPROVED' && <TrophyIcon className="h-3 w-3 text-yellow-5 inline mr-1" />}
            {reward.status === 'APPROVED' ? `Awarded on ${formatDate(reward.updatedDate)}` : statusTag.label}
          </span>
        </div>
        <div className="text-grey-4 text-xs mt-1">Created on {formatDate(reward.createdDate)}</div>

        {reward.rewardSpendAmount > 0 && (
          <div className="text-grey-4 text-xs mt-1">
            Spend Amount: {formatCurrency(reward.rewardSpendAmount)}
          </div>
        )}

        {reward.status === 'PENDING' && (
          <div className="flex items-center gap-4 mt-3">
            <Button className="px-8 py-2 text-base font-medium" variant="default" onClick={() => onApprove(reward.id, BoostRewardAction.APPROVE)}>Approve</Button>
            <button className="text-destructive text-base font-medium hover:underline focus:outline-none" type="button" onClick={() => onApprove(reward.id, BoostRewardAction.REJECT)}>Reject</button>
          </div>
        )}
        {reward.status === 'APPROVED' && reward.secureCode && (
          <div className="text-grey-4 text-xs mt-1">Approved by Admin</div>
        )}
        {reward.secureCode && (
          <div className="flex items-center justify-between bg-grey-1 rounded-2xl px-2 py-2 mt-2">
            <Tooltip>
              <TooltipTrigger>
                <p className="text-primary text-sm font-normal m-0">
                  Discount Code: {reward.secureCode.externalDiscountCode.length > 10 ? `${reward.secureCode.externalDiscountCode.slice(0, 10)}...` : reward.secureCode.externalDiscountCode}
                </p>
              </TooltipTrigger>
              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50">
                {reward.secureCode.externalDiscountCode}
              </TooltipContent>
            </Tooltip>
            <Tag
              label={reward.secureCode.isRedeemed ? 'Redeemed' : 'waiting for creator'}
              bgColor={reward.secureCode.isRedeemed ? 'bg-green-2' : 'bg-blue-2'}
              textColor="text-primary"
            />
          </div>
        )}
        {/* View Order button for PRODUCT_CATALOG PFA_BOOST */}
        {viewOrderButton}
      </div>
    </div>
  );
};

export const MemberBoostDetails = ({ memberBoosts, onClose, refetchData, refresh, selectedMemberConversion, refetchBoostsPerformanceData, offerId }: MemberBoostDetailsProps): JSX.Element => {
  const { [ClientFeature.PFA_BOOST]: isPFABoostEnabled } = useClientFeatures();

  // Set up pfaApolloClient similar to RefreshMemberTable
  const { token } = useAuth();
  const pfaApolloClient = useApolloClient(token);

  const [bulkActionBoostRewards] = useBulkActionBoostRewards({
    onCompleted: (data) => {
      if (data.bulkActionBoostRewards) {
        message.success('Boost reward updated successfully');
        onClose();
        refetchData();
        refresh();
        refetchBoostsPerformanceData();
      } else {
        // refetch the data if bulkActionBoostRewards is false.
        onClose();
        refetchData();
        refresh();
        refetchBoostsPerformanceData();
      }
    },
    onError: (error) => {
      logger.error('Bulk action failed:', error);
    }
  });
  const { bulkApproveRejectOrderRequest } = useBulkApproveRejectOrderRequest({
    client: pfaApolloClient,
  });
  const [openDrawerOrderId, setOpenDrawerOrderId] = React.useState<string | null>(null);
  const [rejectOrderId, setRejectOrderId] = React.useState<string | null>(null);
  const [isRejectAlertOpen, setIsRejectAlertOpen] = React.useState(false);
  const [isRejecting, setIsRejecting] = React.useState(false);

  // TODO: Replace with actual selected members logic if available
  const selectedMembers: readonly TMember[] = [];

  if (!memberBoosts || memberBoosts.length === 0) {
    return (
      <div className="text-center py-8 text-grey-5">
        <p>No reward data available</p>
      </div>
    );
  }

  const getOverallRewardStatus = (boostRewardsForAffiliate: { status: string }[]) => {
    if (!boostRewardsForAffiliate || boostRewardsForAffiliate.length === 0) {
      return { label: 'No rewards', bgColor: 'bg-grey-2', textColor: 'text-primary' };
    }

    // Check if at least one reward is waiting for input (PENDING)
    const hasPending = boostRewardsForAffiliate.some((reward) => reward.status === 'PENDING');
    if (hasPending) {
      return { label: 'Waiting for approval', bgColor: 'bg-yellow-2', textColor: 'text-primary' };
    }

    // Check if at least one reward is awarded (APPROVED)
    const hasApproved = boostRewardsForAffiliate.some((reward) => reward.status === 'APPROVED');
    if (hasApproved) {
      return { label: 'Awarded', bgColor: 'bg-green-2', textColor: 'text-primary' };
    }

    // Check if at least one reward is rejected
    const hasRejected = boostRewardsForAffiliate.some((reward) => reward.status === 'REJECTED');
    if (hasRejected) {
      return { label: 'Rejected', bgColor: 'bg-red-2', textColor: 'text-primary' };
    }

    const hasErrors = boostRewardsForAffiliate.some((reward) => reward.status === 'ERROR');
    if (hasErrors) {
      return { label: 'Error', bgColor: 'bg-red-2', textColor: 'text-primary' };
    }

    // Default fallback
    return { label: 'Unknown', bgColor: 'bg-grey-2', textColor: 'text-primary' };
  };

  const handleApprove = (id: number, action: BoostRewardAction) => {
    if (!id) {
      logger.warn('No Reward selected for bulk action');
      return;
    }

    // Call the bulk action mutation
    bulkActionBoostRewards({
      variables: {
        action,
        boostRewardIds: [id]
      }
    });
  };

  const getBoostTriggerDisplay = (triggerType: string) => {
    const isEvery = triggerType.includes('EVERY');

    if (isEvery) {
      return {
        icon: <ArrowsRotateIcon className="w-3 h-3 text-grey-3 ml-2" />,
        tooltipText: 'Recurring'
      };
    } else {
      return {
        icon: (
          <div className="flex items-center justify-center w-3 h-3 rounded-full bg-grey-2 text-grey-foreground text-[8px] font-semibold ml-2">
            1
          </div>
        ),
        tooltipText: 'One time'
      };
    }
  };

  const getBoostDescription = (boost: GetAllBoostData_boostDataForAllAffiliatesInOffer_boost) => {
    const baseType = boost.boostType === 'DISCOUNT_BOOST' ? 'Discount Boost' : boost.boostType === BOOST_TYPE.PFA_BOOST ? 'Free Product' : boost.boostType === BOOST_TYPE.CASHBACK_BOOST ? 'Cashback Boost' : boost.boostType;
    const parts = [baseType];

    if (boost.triggerType === 'ON_X_CONVERSION') {
      parts.push(`on ${boost.triggerValue} conversion${boost.triggerValue > 1 ? 's' : ''}`);
    } else if (boost.triggerType === 'ON_EVERY_X_CONVERSION') {
      parts.push(`for every ${boost.triggerValue} conversion${boost.triggerValue > 1 ? 's' : ''}`);
    }

    if (boost.discountOffer?.promos?.[0]) {
      const promo = boost.discountOffer.promos[0];
      if (promo.priceRuleType === 'PERCENTAGE') {
        parts.push(`${promo.priceRuleAmount}% off`);
      }
    }

    if (boost.maxLimit) {
      parts.push(`(max ${boost.maxLimit})`);
    }

    return parts.join(' ');
  };

  return (
    <>
      {memberBoosts.map((boostData, index) => {
        const { boost, boostRewardsForAffiliate: allRewards } = boostData;
        const boostRewardsForAffiliate = allRewards?.filter((reward) => {
          const isMatch = selectedMemberConversion?.affiliateId && reward.affiliateId === selectedMemberConversion.affiliateId;
          return isMatch;
        });
        const hasRewards = boostRewardsForAffiliate && boostRewardsForAffiliate.length > 0;

        return (
          <Accordion key={index} type="single" collapsible className="w-full">
            <AccordionItem value={`Reward-${boost.id}`} className="pt-2  border border-grey-2 rounded-xl px-4">
              <AccordionTrigger className="p-0 flex items-center hover:no-underline gap-2 m-0">
                {getBoostIcon(boost.boostType)}
                <div className="flex flex-1 items-center justify-between">
                  <h3 className="text-grey-6 font-medium flex items-center m-0">
                    {boost.label}
                    <Tooltip>
                      <TooltipTrigger>
                        {boost.triggerType ? getBoostTriggerDisplay(boost.triggerType).icon : null}
                      </TooltipTrigger>
                      <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                        {boost.triggerType ? getBoostTriggerDisplay(boost.triggerType).tooltipText : null}
                      </TooltipContent>
                    </Tooltip>
                  </h3>
                  <Tag
                    label={getOverallRewardStatus(boostRewardsForAffiliate).label}
                    bgColor={getOverallRewardStatus(boostRewardsForAffiliate).bgColor}
                    textColor="text-primary"
                  />
                </div>
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-2 mt-2 pb-4">
                {/* PRODUCT_CATALOG Boost Approval Buttons with Order Status Check */}
                {isPFABoostEnabled && boostData?.boost?.boostMetadata?.pfaBoostType === 'PRODUCT_CATALOG' && hasRewards && boostRewardsForAffiliate.map((reward) => (
                  <OrderCard
                    key={reward.id}
                    reward={reward}
                    onViewOrder={setOpenDrawerOrderId}
                    onReject={(pfaOrderId) => {
                      setRejectOrderId(pfaOrderId);
                      setIsRejectAlertOpen(true);
                    }}
                    isRejecting={isRejecting}
                  />
                ))}

                <p className="text-grey-5 font-medium text-sm m-0">Reward Details</p>
                <div className="border-b" />
                <p className="text-grey-5 font-normal text-sm m-0">
                  {getBoostDescription(boost)}
                  {boost.isActive && <span className="text-grey-5 text-xs font-normal ml-2">Active</span>}
                </p>

                {hasRewards ? (
                  boostRewardsForAffiliate.map((reward, rewardIndex) => (
                    <RewardItem
                      key={reward.id}
                      reward={reward}
                      boost={boost}
                      rewardIndex={rewardIndex}
                      totalRewards={boostRewardsForAffiliate.length}
                      onApprove={handleApprove}
                    />
                  ))
                ) : (
                  <div className="text-grey-4 text-sm py-4 text-center">
                    No rewards available for this Reward
                  </div>
                )}

                {/* PFAApprovalDrawer for PRODUCT_CATALOG */}
                {openDrawerOrderId && (
                  <PFAApprovalDrawer
                    open={!!openDrawerOrderId}
                    setIsPFAApprovalDrawerOpen={(open) => !open && setOpenDrawerOrderId(null)}
                    selectedMembers={selectedMembers}
                    offerId={offerId}
                    refresh={refresh}
                    pfaOrderIds={[openDrawerOrderId]}
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        );
      })}

      {/* AlertPFAReject for immediate reject confirmation */}
      <AlertPFAReject
        isOpen={isRejectAlertOpen}
        onOpenChange={setIsRejectAlertOpen}
        totalMembers={1}
        onConfirm={async () => {
          if (!rejectOrderId) return;
          setIsRejecting(true);
          try {
            await bulkApproveRejectOrderRequest({
              variables: {
                params: {
                  operation: BulkApproveRejectOperation.REJECT,
                  orderInputs: [{ orderId: rejectOrderId, creatorOrderRequest: null }],
                },
              },
            });
            toast({
              title: 'Order Rejected',
              description: 'The order was successfully rejected.',
              variant: 'success',
            });
            setIsRejectAlertOpen(false);
            setRejectOrderId(null);
            // Automatically refetch data after successful rejection
            refetchData && refetchData();
            refresh && refresh();
            refetchBoostsPerformanceData && refetchBoostsPerformanceData();
          } catch (error) {
            toast({
              title: 'Error',
              description: 'Failed to reject the order. Please try again.',
              variant: 'destructive',
            });
          } finally {
            setIsRejecting(false);
          }
        }}
      />
    </>
  );
};

export default MemberBoostDetails;
